<template>
    <div>
        <transition name="page">
            <v-container class="pa-5" fluid>
                <UpdateUserDialog
                    :user-data="user"
                    :production-departments="productionDepartments"
                    :is-open="updateUserDialog"
                    @on-close="closeUpdateUserDialog"
                    @on-save="closeUpdateUserDialog"
                />
                <CreateUserDialog
                    :is-open="createUserDialog"
                    @on-close="closeAddUserDialog"
                    @user-created="handleUserCreated"
                />

                <page-title>Lista użytkowników</page-title>

                <v-row>
                    <v-col cols="12">
                        <v-container fluid class="pa-0">
                            <v-row justify="end" dense>
                                <v-col cols="auto">
                                    <v-btn 
                                        color="secondary"
                                        medium
                                        data-sentry-label="Dodaj użytkownika"
                                        @click="createUser"
                                    >
                                        <v-icon left>
                                            mdi-plus
                                        </v-icon>
                                        Dodaj użytkownika
                                    </v-btn>
                                </v-col>
                                <v-col cols="12" sm="5" md="4" lg="3" xl="2">
                                    <v-select
                                        v-model="filters.productionDepartment_id"
                                        :items="productionDepartments"
                                        item-value="id"
                                        item-text="name"
                                        placeholder="Działy..."
                                        no-data-text="Brak danych..."
                                        hide-details
                                        solo
                                        clearable
                                        dense
                                        @change="changeFilter"
                                    />
                                </v-col>
                                <v-col cols="12" sm="5" md="4" lg="3" xl="2">
                                    <v-text-field
                                        v-model="filters.search"
                                        append-icon="mdi-magnify"
                                        label="Szukaj..."
                                        single-line
                                        clearable
                                        hide-details
                                        solo
                                        dense
                                        @keyup.enter="changeFilter"
                                        @click:append="changeFilter"
                                        @click:clear="clearFilter('search')"
                                    />
                                </v-col>
                            </v-row>
                        </v-container>
                    </v-col>
                </v-row>

                <v-row>
                    <v-col cols="12">
                        <v-card>
                            <v-data-table
                                :headers="headers"
                                :items="users"
                                :loading="usersLoading"
                                :footer-props="footerProps"
                                :server-items-length="pagination.itemsLength"
                                :page.sync="pagination.page"
                                :mobile-breakpoint="0"
                                name="usersData"
                                @pagination="updatePagination"
                            >
                                <template #item="{item}">
                                    <tr>
                                        <td>
                                            {{ item.username }}
                                        </td>
                                        <td>
                                            {{ parseValue(item.firstName + ' ' + item.lastName) }}
                                        </td>
                                        <td>
                                            {{ parseValue(item.email) }}
                                        </td>
                                        <td>
                                            {{ productionDepartmentName(item) }}
                                        </td>
                                        <td>
                                            {{ formatDate(item.lastLogin) }}
                                        </td>
                                        <td class="text-center">
                                            <v-icon
                                                v-if="item.isActive"
                                                small
                                                color="success"
                                            >
                                                mdi-check
                                            </v-icon>
                                            <v-icon v-else small color="error">
                                                mdi-close
                                            </v-icon>
                                        </td>
                                        <td class="text-center">
                                            <div class="d-inline-flex">
                                                <v-tooltip :open-delay="tooltipDelay" top>
                                                    <template #activator="{on}">
                                                        <v-btn
                                                            icon
                                                            data-sentry-label="Edycja"
                                                            v-on="on"
                                                            @click="showEditUserDialog(item.id)"
                                                        >
                                                            <v-icon color="blue">
                                                                mdi-pencil
                                                            </v-icon>
                                                        </v-btn>
                                                    </template>
                                                    <span>Edycja</span>
                                                </v-tooltip>
                                            </div>
                                        </td>
                                    </tr>
                                </template>
                            </v-data-table>
                        </v-card>
                    </v-col>
                </v-row>
            </v-container>
        </transition>
    </div>
</template>

<script>
import PageTitle from '@/components/common/PageTitle'

import filters from '@/mixins/dataTableFilters'
import pagination from '@/mixins/dataTablePagination'
import tooltipDelay from '@/mixins/tooltip'
import dateFormatter from '@/mixins/dateFormatter'

import { errorHandler } from '@/utils/errorHandler'

export default {
    name: 'UsersList',
    components: {
        PageTitle,
        UpdateUserDialog: () => import('./UpdateUserDialog'),
        CreateUserDialog: () => import('./CreateUserDialog'),
    },
    mixins: [
        pagination,
        tooltipDelay,
        filters,
        dateFormatter
    ],
    beforeRouteLeave(to, from, next) {
        this.$store.dispatch('administration/clearUsersFilters')
        next()
    },
    data() {
        return {
            user: {},
            updateUserDialog: false,
            createUserDialog: false,
            headers: [
                {
                    text: 'Nazwa użytkownika',
                    value: 'name',
                    sortable: false
                },
                {
                    text: 'Imię i nazwisko',
                    value: 'lastName',
                    sortable: false
                },
                {
                    text: 'E-mail',
                    value: 'email',
                    sortable: false
                },
                {
                    text: 'Dział',
                    value: 'department',
                    sortable: false
                },
                {
                    text: 'Ostatnie logowanie',
                    value: 'lastLogin',
                    sortable: false,
                },
                {
                    text: 'Czy aktywny?',
                    value: 'isActive',
                    sortable: false,
                    align: 'center',
                    width: 120
                },
                {
                    text: 'Akcje',
                    value: 'actions',
                    sortable: false,
                    align: 'center',
                    width: 100
                }
            ]
        }
    },
    computed: {
        users() {
            return this.$store.state.administration.users.users
        },
        usersLoading() {
            return this.$store.state.administration.users.usersLoading
        },
        savedFilters() {
            return this.$store.state.administration.users.filters
        },
        productionDepartments() {
            return this.$store.state.administration.users.productionDepartments
        },
        filterQuery() {
            const filters = this.filters
            let query = ''

            for (let item in filters) {
                if (filters[item] != null && filters[item] != '') {
                    query = query.concat('&', `${item}=${filters[item]}`)
                }
            }

            return query
        },
    },
    mounted() {
        this.getUsers()
        Promise.all([
            this.$store.dispatch('administration/getProductionDepartments'),
            this.$store.dispatch('administration/getUserPositions'),
            this.$store.dispatch('administration/getUserSquads'),
            this.$store.dispatch('administration/getUserRoles'),
        ])
            .catch(error => {
                errorHandler(error)
            })
    },
    methods: {
        createUser() {
            this.showCreateUserDialog()
        },
        handleUserCreated(newUser) {
            this.user = newUser
            this.updateUserDialog = true
        },
        getUsers() {
            this.$store.dispatch('administration/getUsers', {
                paginate: this.paginate(),
                query: this.filterQuery
            })
                .then(response => {
                    this.handlePaginationResponse(response)
                })
                .catch(error => {
                    errorHandler(error)
                })
        },
        showCreateUserDialog() {
            this.createUserDialog = true
        },
        showEditUserDialog(userId) {
            this.$store.dispatch('toggleCircularLoader', true)
            Promise.all([
                this.$store.dispatch('administration/getCostCenterNumbers'),
                this.$store.dispatch('administration/getUser', userId)
            ])
                .then(responses => {
                    this.user = responses[1].data

                    const query = this.user.userprofile?.productionDepartment ? `?productionDepartment_id=${this.user.profile.productionDepartmentId}` : ''

                    this.$store.dispatch('configurations/getMachines', { query: query })
                        .then(() => {
                            this.updateUserDialog = true
                        })
                        .catch(error => {
                            errorHandler(error)
                        })
                        .finally(() => {
                            this.$store.dispatch('toggleCircularLoader', false)
                        })
                })
                .catch(error => errorHandler(error))
        },
        closeUpdateUserDialog() {
            this.updateUserDialog = false
            this.user = {}

            setTimeout(() => {
                this.user = {}
                this.getUsers()
            }, 500)
        },
        closeAddUserDialog() {
            this.createUserDialog = false
        },
        productionDepartmentName(user) {
            return user.profile?.productionDepartment?.name || '-'
        },
        parseValue(value) {
            return value ? value.trim() : '-'
        },
        changeFilter() {
            this.$store.dispatch('administration/setUsersFilters', this.filters)
            this.resetPagination()
            this.getUsers()
        },
        updatePagination(pagination) {
            this.pagination = pagination
            if (pagination.pageCount !== 0 && !this.paginationSettings.paginationPending) {
                this.getUsers()
            }
            this.clearPaginationPending()
        }
    }
}
</script>
